
import { pluralRu } from '@/shared/lib/plural';

export type TourId = 'requester' | 'staff';

export type TourSide = 'bottom' | 'right';

export interface TourStepContext {
  hasPermission: (permission: string) => boolean;
  canAccessAdmin: boolean;
  pendingApprovals: number;
  messengerUnread: number;
  hasDraft: boolean;
}

export type TourText = string | ((ctx: TourStepContext) => string);

export const tourText = (text: TourText, ctx: TourStepContext): string =>
  typeof text === 'function' ? text(ctx) : text;

export interface TourStep {
  id: string;
  anchor: string | null;
  side?: TourSide;
  inMenu?: boolean;
  when?: (ctx: TourStepContext) => boolean;
  title: string;
  body: TourText;
  empty?: { anchor: string; body: TourText };
}

export interface TourSection {
  path: string;
  prefix?: boolean;
  also?: string[];
  trigger?: string;
  title: string;
  steps: TourStep[];
}

export interface Tour {
  id: TourId;
  version: number;
  steps: TourStep[];
  sections: TourSection[];
  outro: TourStep[];
}

export const SHOW_ITILIUM_NOTE = true;

const ITILIUM_NOTE =
  'Открытые заявки из Итилиум уже перенесены сюда. Если какой-то нет в списке — создайте её заново через «Новое обращение» или сообщите об этом инженерам.';

const welcome = (body: string): string => (SHOW_ITILIUM_NOTE ? `${body}\n\n${ITILIUM_NOTE}` : body);

const PROFILE_STEP: TourStep = {
  id: 'profile',
  anchor: 'user-menu',
  side: 'bottom',
  inMenu: true,
  title: 'Профиль и настройки',
  body: 'Здесь настройки уведомлений и выход. Экскурсию можно пройти заново из меню профиля.',
};

const SEARCH_HINT = 'Ctrl+K, на Mac — ⌘K';


const TICKETS_EMPTY = {
  anchor: 'list-empty',
  body: 'Обращений пока нет. Новое создаётся кнопкой «+» вверху — оно появится здесь, и тут же можно следить за статусом и переписываться с инженером.',
};

const TICKETS_TABS: TourStep = {
  id: 'tickets-tabs',
  anchor: 'list-tabs',
  title: 'Открытые и все',
  body: 'Сначала видны открытые заявки. «Все» покажет и закрытые — к решению можно вернуться и перечитать его.',
};

const DRAFTS: TourStep = {
  id: 'drafts',
  anchor: 'drafts',
  when: (ctx) => ctx.hasDraft,
  title: 'Незаконченное обращение',
  body: 'Черновик, который вы не отправили. Продолжите с того же места — всё заполненное сохранено.',
};

const APPROVALS: TourSection = {
  path: '/approvals',
  title: 'Согласования',
  steps: [
    {
      id: 'approvals-list',
      anchor: 'ticket-row',
      title: 'Ждут вашего решения',
      body: (ctx) =>
        ctx.pendingApprovals > 0
          ? `Сейчас решения ждут ${ctx.pendingApprovals} ${pluralRu(ctx.pendingApprovals, 'заявка', 'заявки', 'заявок')}. Откройте заявку — согласовать или отклонить можно прямо в ней.`
          : 'Заявки, где вас назначили согласующим. Откройте заявку — согласовать или отклонить можно прямо в ней.',
      empty: {
        anchor: 'list-empty',
        body: 'Сейчас согласовывать нечего. Когда вас назначат согласующим, заявка появится здесь, а у раздела в меню загорится счётчик.',
      },
    },
  ],
};

const COMMITTEE: TourSection = {
  path: '/committee',
  title: 'Комитет',
  steps: [
    {
      id: 'committee',
      anchor: null,
      title: 'Комитет по изменениям',
      body: 'Изменения на рассмотрении комитета: ваши заявки и заявки ключевых пользователей, закреплённых за вами, во всех статусах. Нажмите на строку, чтобы открыть заявку.',
    },
  ],
};

const INSTRUCTIONS: TourSection = {
  path: '/instructions',
  title: 'Инструкции',
  steps: [
    {
      id: 'instructions-search',
      anchor: 'instructions-search',
      title: 'Справочный центр',
      body: 'Статьи с ответами на частые вопросы. Найдите нужную поиском или пролистайте каталог — часто это быстрее, чем ждать инженера.',
    },
  ],
};

const CATALOG: TourSection = {
  path: '/categories',
  prefix: true,
  trigger: 'create-ticket',
  title: 'Новое обращение',
  steps: [
    {
      id: 'catalog-search',
      anchor: 'catalog-search',
      when: (ctx) => !ctx.hasDraft,
      title: 'Каталог услуг',
      body: 'Выберите категорию — внутри инструкции, материалы и форма обращения. Не знаете, куда отнести задачу, — найдите поиском или опишите своими словами: ссылка в конце каталога.',
    },
    {
      id: 'catalog-draft',
      anchor: null,
      when: (ctx) => ctx.hasDraft,
      title: 'Незаконченное обращение',
      body: 'Кнопка вернула вас к черновику — всё заполненное на месте. Когда отправите его, та же кнопка откроет каталог услуг.',
    },
  ],
};

const MENU_OUTRO: TourStep[] = [
  {
    id: 'menu-profile',
    anchor: 'menu-profile',
    title: 'Профиль и настройки',
    body: 'Ваш профиль внизу меню. Рядом — шестерёнка с настройками уведомлений и выход.',
  },
  {
    id: 'menu-tour',
    anchor: 'menu-tour',
    title: 'Экскурсия заново',
    body: 'Компас запускает эту экскурсию ещё раз — если захотите освежить, где что лежит.',
  },
];

export const TOURS: Record<TourId, Tour> = {
  requester: {
    id: 'requester',
    version: 1,
    steps: [
      {
        id: 'welcome',
        anchor: null,
        title: 'Добро пожаловать в портал поддержки',
        body: welcome(
          'Здесь вы создаёте обращения в ИТ, следите за их ходом и переписываетесь с инженером. Покажем главное — это займёт минуту.',
        ),
      },
      {
        id: 'create',
        anchor: 'create-ticket',
        side: 'bottom',
        title: 'Новое обращение',
        body: 'Опишите проблему или выберите услугу из каталога. Незаконченное обращение сохранится черновиком — эта же кнопка вернёт к нему.',
      },
      {
        id: 'tickets',
        anchor: 'nav:/tickets',
        side: 'right',
        inMenu: true,
        title: 'Обращения',
        body: 'Все ваши заявки: статус, срок и переписка с инженером. Когда инженер ждёт вашего ответа, карточка заявки подсветится.',
      },
      {
        id: 'approvals',
        anchor: 'nav:/approvals',
        side: 'right',
        inMenu: true,
        title: 'Согласования',
        body: 'Заявки, которые ждут вашего решения. Счётчик рядом с разделом покажет, сколько их.',
      },
      {
        id: 'search',
        anchor: 'search',
        side: 'bottom',
        title: 'Поиск',
        body: `Найдите заявку по номеру или теме, перейдите в нужный раздел. Быстрее — с клавиатуры: ${SEARCH_HINT}.`,
      },
      {
        id: 'bug',
        anchor: 'bug-report',
        side: 'bottom',
        title: 'Сообщить о проблеме',
        body: 'Что-то в портале работает не так или есть идея, как сделать удобнее, — напишите прямо отсюда.',
      },
      PROFILE_STEP,
    ],
    sections: [
      {
        path: '/tickets',
        title: 'Обращения',
        steps: [
          {
            id: 'tickets-list',
            anchor: 'ticket-row',
            title: 'Ваши обращения',
            body: 'Каждая карточка — заявка: статус, срок и последнее сообщение. Нажмите на неё, чтобы открыть переписку с инженером. Когда инженер ждёт вашего ответа, карточка подсвечивается.',
            empty: TICKETS_EMPTY,
          },
          TICKETS_TABS,
          DRAFTS,
        ],
      },
      CATALOG,
      INSTRUCTIONS,
      APPROVALS,
      COMMITTEE,
    ],
    outro: MENU_OUTRO,
  },
  staff: {
    id: 'staff',
    version: 1,
    steps: [
      {
        id: 'welcome',
        anchor: null,
        title: 'Добро пожаловать в портал поддержки',
        body: welcome(
          'Здесь вы ведёте заявки, планируете работу и общаетесь с коллегами. Покажем, где что лежит.',
        ),
      },
      {
        id: 'workspace',
        anchor: 'nav:/tickets/workspace',
        side: 'right',
        inMenu: true,
        title: 'Рабочий стол',
        body: 'Заявки в работе у вас и вашей группы: что назначено, что горит по SLA, что ждёт ответа.',
      },
      {
        id: 'tickets',
        anchor: 'nav:/tickets',
        side: 'right',
        inMenu: true,
        title: 'Обращения',
        body: 'Портал глазами заявителя: ваши собственные заявки и всё, что вы можете видеть.',
      },
      {
        id: 'messenger',
        anchor: 'nav:/messenger',
        side: 'right',
        inMenu: true,
        title: 'Мессенджер',
        body: 'Переписка с коллегами и каналы групп. Непрочитанное — счётчиком рядом с разделом.',
      },
      {
        id: 'sprints',
        anchor: 'nav:/sprints',
        side: 'right',
        inMenu: true,
        when: (ctx) => ctx.hasPermission('view_sprints'),
        title: 'Спринты',
        body: 'Задачи разработки: доска, календарь и таймлайн спринтов вашей группы.',
      },
      {
        id: 'analytics',
        anchor: 'nav:/analytics',
        side: 'right',
        inMenu: true,
        title: 'Аналитика и KPI',
        body: 'Отчёты по заявкам за выбранный период. Ниже в меню — KPI: начисления и рейтинг инженеров за месяц.',
      },
      {
        id: 'search',
        anchor: 'search',
        side: 'bottom',
        title: 'Поиск и команды',
        body: `Найти заявку или раздел и выполнить действие, не отрываясь от клавиатуры: ${SEARCH_HINT}.`,
      },
      {
        id: 'admin',
        anchor: 'nav:/admin',
        side: 'right',
        inMenu: true,
        when: (ctx) => ctx.canAccessAdmin,
        title: 'Администрирование',
        body: 'Пользователи, права, флоу и справочники — те разделы, на которые у вас есть права.',
      },
      PROFILE_STEP,
    ],
    sections: [
      {
        path: '/tickets',
        title: 'Обращения',
        steps: [
          {
            id: 'tickets-list',
            anchor: 'ticket-row',
            title: 'Ваши обращения',
            body: 'Заявки, которые вы подали сами, — как у любого сотрудника: статус, срок и переписка с исполнителем. Рабочие заявки — дальше, на «Рабочем столе».',
            empty: TICKETS_EMPTY,
          },
          TICKETS_TABS,
          DRAFTS,
        ],
      },
      CATALOG,
      INSTRUCTIONS,
      APPROVALS,
      COMMITTEE,
      {
        path: '/tickets/workspace',
        title: 'Рабочий стол',
        steps: [
          {
            id: 'workspace-views',
            anchor: 'workspace-views',
            title: 'Представления',
            body: '«Все» — общий поток, «Новые» — заявки без исполнителя, «Мои» — назначенные на вас, «Группы» — заявки групп, в которых вы состоите.',
          },
          {
            id: 'workspace-list',
            anchor: 'ticket-row',
            title: 'Заявка в списке',
            body: 'Статус, срок по SLA и исполнитель видны сразу. Взять заявку себе можно прямо из списка, остальное — в карточке.',
            empty: {
              anchor: 'list-empty',
              body: 'В этом представлении сейчас пусто. Переключите вкладку выше или сбросьте фильтры.',
            },
          },
          {
            id: 'workspace-filters',
            anchor: 'list-filters',
            title: 'Фильтры',
            body: 'Статус, SLA, приоритет и группа. Фильтры живут в адресе страницы — ссылкой можно поделиться с коллегой.',
          },
        ],
      },
      {
        path: '/messenger',
        title: 'Мессенджер',
        steps: [
          {
            id: 'messenger-channels',
            anchor: 'messenger-channels',
            title: 'Каналы групп',
            body: (ctx) =>
              `Канал есть у каждой группы, в которой вы состоите, — туда пишут всей группой. Нажмите на канал, чтобы открыть переписку.${
                ctx.messengerUnread > 0 ? ` Сейчас непрочитанных: ${ctx.messengerUnread}.` : ''
              }`,
          },
        ],
      },
      {
        path: '/sprints',
        title: 'Спринты',
        steps: [
          {
            id: 'sprint-views',
            anchor: 'sprint-views',
            title: 'Спринт группы',
            body: 'Доска, календарь, таймлайн, отчёт, версии и события текущего спринта. На доске задачи переходят по колонкам статусов.',
            empty: {
              anchor: 'sprint-none',
              body: 'У группы пока нет спринта. Его заводит руководитель группы — после этого здесь появятся доска, календарь и таймлайн.',
            },
          },
        ],
      },
      {
        path: '/analytics',
        title: 'Аналитика',
        steps: [
          {
            id: 'analytics-filters',
            anchor: 'analytics-filters',
            title: 'Период и срезы',
            body: (ctx) =>
              `Период, группа и категория — от них считается всё на странице.${
                ctx.hasPermission('view_sprints') ? ' Вкладка «Спринты» — та же аналитика по задачам разработки.' : ''
              }`,
          },
          {
            id: 'analytics-stats',
            anchor: 'analytics-stats',
            title: 'Главные цифры',
            body: 'Сколько заявок создано и решено, медиана реакции и просрочка решения за период. Ниже — графики, вверху страницы — выгрузка в CSV.',
          },
        ],
      },
      {
        path: '/kpi',
        title: 'KPI',
        steps: [
          {
            id: 'kpi-table',
            anchor: 'kpi-table',
            title: 'Рейтинг за месяц',
            body: 'Очки инженеров за выбранный месяц. Нажмите на строку — откроется, из чего они сложились.',
          },
          {
            id: 'kpi-rules',
            anchor: 'kpi-rules',
            title: 'Как считаются очки',
            body: (ctx) =>
              `За что и сколько начисляется.${
                ctx.hasPermission('manage_kpi') ? ' Рядом — «Настройки»: правила начисления меняете вы.' : ''
              }`,
          },
        ],
      },
      {
        path: '/admin',
        prefix: true,
        title: 'Администрирование',
        steps: [
          {
            id: 'admin-nav',
            anchor: 'admin-nav',
            title: 'Подразделы',
            body: 'Пользователи, права, флоу и справочники — здесь только те, на которые у вас есть права. Лента подразделов листается вбок.',
          },
        ],
      },
    ],
    outro: MENU_OUTRO,
  },
};

export function tourFor(hasPermission: (permission: string) => boolean): Tour {
  return hasPermission('view_tickets') ? TOURS.staff : TOURS.requester;
}
